import { isHealthDataConsentCurrent, normalizeNutritionPreferences } from "com";
import { PEOPLE_CONSTRAINTS, DAY_IDS, MEAL_IDS, DIET_IDS } from "@/lib/constants/preferences";
import type { DayId, MealId, HouseholdPresetId } from "@/lib/constants/preferences";

export interface PeoplePreferences {
  [key: string]: {
    [meal: string]: number;
  };
}

export interface UserPreferences {
  people: PeoplePreferences;
  diets: string[];
  allergens: string[];
  /** Optional bias applied to menu scoring. Absent on payloads saved before the feature shipped. */
  nutritionPreferences?: string[];
  /**
   * Consent to process health data (allergies). From the API: true only if a consent for the
   * current text is on record. Sent back on save; undefined is never sent (older clients).
   */
  healthDataConsent?: boolean;
}

const HOUSEHOLD_PRESET_GUESTS: Record<Exclude<HouseholdPresetId, "custom">, number> = {
  solo: 1,
  couple: 2,
  coupleOneKid: 3,
  coupleTwoKids: 4,
};

/**
 * Adults / children split per household preset. The total must match
 * `HOUSEHOLD_PRESET_GUESTS` for the same preset. Consumed by the dual-audience
 * matrix to seed the split when the user picks a preset (otherwise the dual
 * matrix would derive `adults = total, children = 0` from `deriveFromBase`).
 */
export const HOUSEHOLD_PRESET_COMPOSITION: Record<
  Exclude<HouseholdPresetId, "custom">,
  { adults: number; children: number }
> = {
  solo: { adults: 1, children: 0 },
  couple: { adults: 2, children: 0 },
  coupleOneKid: { adults: 2, children: 1 },
  coupleTwoKids: { adults: 2, children: 2 },
};

const ALLOWED_DIET_IDS = new Set<string>(DIET_IDS);

const sanitizeDiets = (diets?: string[] | null): string[] => {
  if (!Array.isArray(diets) || diets.length === 0) {
    return [];
  }

  return [...new Set(diets.map((diet) => String(diet || "").toLowerCase().trim()).filter((diet) => ALLOWED_DIET_IDS.has(diet)))];
};

export const normalizePeoplePreferences = (preferences?: Partial<PeoplePreferences> | null): PeoplePreferences => {
  const normalizedPreferences: PeoplePreferences = {};

  DAY_IDS.forEach((day) => {
    normalizedPreferences[day] = {};

    MEAL_IDS.forEach((meal) => {
      const currentValue = preferences?.[day]?.[meal];
      const parsedValue = Number.isInteger(currentValue) ? Number(currentValue) : PEOPLE_CONSTRAINTS.DEFAULT;

      normalizedPreferences[day][meal] = Math.max(
        PEOPLE_CONSTRAINTS.MIN,
        Math.min(PEOPLE_CONSTRAINTS.MAX, parsedValue)
      );
    });
  });

  return normalizedPreferences;
};

/**
 * Create default people preferences structure
 */
export const createDefaultPeoplePreferences = (): PeoplePreferences => {
  return normalizePeoplePreferences();
};

/**
 * Create a full week matrix from a household preset.
 * The backend payload stays unchanged; only the authoring experience changes.
 */
export const derivePeoplePreferencesFromPreset = (
  presetId: Exclude<HouseholdPresetId, "custom">
): PeoplePreferences => {
  const guests = HOUSEHOLD_PRESET_GUESTS[presetId];
  return normalizePeoplePreferences(
    DAY_IDS.reduce<PeoplePreferences>((accumulator, day) => {
      accumulator[day] = MEAL_IDS.reduce<Record<string, number>>((mealAccumulator, meal) => {
        mealAccumulator[meal] = guests;
        return mealAccumulator;
      }, {});

      return accumulator;
    }, {})
  );
};

export const matchesHouseholdPreset = (
  peoplePreferences: PeoplePreferences,
  presetId: Exclude<HouseholdPresetId, "custom">
): boolean => {
  const expectedGuests = HOUSEHOLD_PRESET_GUESTS[presetId];

  return DAY_IDS.every((day) =>
    MEAL_IDS.every((meal) => Number(peoplePreferences?.[day]?.[meal] ?? PEOPLE_CONSTRAINTS.MIN) === expectedGuests)
  );
};

/**
 * Infer the closest household preset from a stored weekly matrix.
 * Anything that does not match a standard household shape falls back to "custom".
 */
export const inferHouseholdPreset = (peoplePreferences: PeoplePreferences): HouseholdPresetId => {
  const normalizedPreferences = normalizePeoplePreferences(peoplePreferences);
  const matchingPreset = (Object.keys(HOUSEHOLD_PRESET_GUESTS) as Array<Exclude<HouseholdPresetId, "custom">>).find(
    (presetId) => matchesHouseholdPreset(normalizedPreferences, presetId)
  );

  return matchingPreset ?? "custom";
};

export const hasCustomWeekOverrides = (
  peoplePreferences: PeoplePreferences,
  presetId: HouseholdPresetId
): boolean => {
  if (presetId === "custom") {
    return false;
  }

  return !matchesHouseholdPreset(peoplePreferences, presetId);
};

export const countConfiguredMealSlots = (peoplePreferences: PeoplePreferences): number => {
  const normalizedPreferences = normalizePeoplePreferences(peoplePreferences);

  return DAY_IDS.reduce(
    (configuredCount, day) =>
      configuredCount + MEAL_IDS.filter((meal) => Number(normalizedPreferences[day][meal] ?? 0) > 0).length,
    0
  );
};

export const calculateAverageGuestsPerMeal = (peoplePreferences: PeoplePreferences): number => {
  const normalizedPreferences = normalizePeoplePreferences(peoplePreferences);
  const totalGuests = DAY_IDS.reduce(
    (guestCount, day) =>
      guestCount + MEAL_IDS.reduce((mealGuestCount, meal) => mealGuestCount + Number(normalizedPreferences[day][meal] ?? 0), 0),
    0
  );

  return Math.round(totalGuests / (DAY_IDS.length * MEAL_IDS.length));
};

/**
 * Update people count for a specific day and meal
 * Returns new state (immutable)
 * Allows 0 for meals that can be skipped (e.g., no breakfast, no snack)
 */
export const updatePeopleCount = (
  currentPreferences: PeoplePreferences,
  day: DayId,
  meal: MealId,
  value: number | string
): PeoplePreferences => {
  const parsedValue = parseInt(String(value));
  const numValue = Math.max(
    PEOPLE_CONSTRAINTS.MIN,
    Math.min(PEOPLE_CONSTRAINTS.MAX, isNaN(parsedValue) ? PEOPLE_CONSTRAINTS.DEFAULT : parsedValue)
  );
  const normalizedPreferences = normalizePeoplePreferences(currentPreferences);

  return {
    ...normalizedPreferences,
    [day]: {
      ...normalizedPreferences[day],
      [meal]: numValue,
    },
  };
};

/**
 * Backend API GET Response Type (what API returns)
 * Matches app/src/logic/preferences/types.ts UserPreferences
 */
interface BackendPreferences {
  num_guests?: PeoplePreferences;
  selected_diets?: string[];
  excluded_allergens?: number[]; // API returns allergen IDs as numbers
  nutrition_preferences?: string[];
  health_data_consent?: { accepted: boolean; at: string | null; version: string | null };
}

/**
 * Backend API POST Payload Type (what API accepts)
 * API accepts allergen names as strings and converts to IDs internally
 */
interface BackendPreferencesPayload {
  num_guests?: PeoplePreferences;
  selected_diets?: string[];
  excluded_allergens?: (string | number)[]; // API accepts both names (strings) and IDs (numbers)
  nutrition_preferences?: string[];
  health_data_consent?: boolean;
}

/**
 * Map backend GET response to frontend structure
 * Converts number[] allergen IDs to string[] names for UI
 */
export const mapBackendToFrontend = (backendPrefs: BackendPreferences): UserPreferences => {
  return {
    people: normalizePeoplePreferences(backendPrefs.num_guests),
    diets: sanitizeDiets(backendPrefs.selected_diets),
    allergens: backendPrefs.excluded_allergens?.map(String) || [], // Convert IDs to strings
    nutritionPreferences: normalizeNutritionPreferences(backendPrefs.nutrition_preferences),
    healthDataConsent: isHealthDataConsentCurrent(backendPrefs.health_data_consent),
  };
};

/**
 * Map frontend preferences to backend POST payload
 * Backend accepts allergen names as strings and converts them to IDs internally
 */
export const mapFrontendToBackend = (frontendPrefs: UserPreferences): BackendPreferencesPayload => {
  // Sanitize allergens: filter out empty, null, undefined values
  // Keep as strings - backend will map names to IDs
  const sanitizedAllergens = (frontendPrefs.allergens || [])
    .filter((allergen) => allergen !== null && allergen !== undefined && allergen !== "")
    .map(String); // Ensure strings (allergen names like "eggs", "gluten")

  return {
    num_guests: normalizePeoplePreferences(frontendPrefs.people),
    selected_diets: sanitizeDiets(frontendPrefs.diets),
    excluded_allergens: sanitizedAllergens,
    nutrition_preferences: normalizeNutritionPreferences(frontendPrefs.nutritionPreferences),
    ...(frontendPrefs.healthDataConsent !== undefined && { health_data_consent: frontendPrefs.healthDataConsent }),
  };
};

/**
 * Toggle item in array (immutable)
 */
export const toggleArrayItem = <T>(array: T[], item: T, shouldAdd: boolean): T[] => {
  return shouldAdd ? [...array, item] : array.filter((i) => i !== item);
};

/**
 * Validate that at least one diet is selected
 */
export const hasSelectedDiet = (preferences: UserPreferences): boolean => {
  return preferences.diets.length > 0;
};
